(() => {
  // ---------------------------------------------------------------------------
  // Month 1 PDP Conversion Bundle — Experiment: pdp_m1
  //
  // Tests the Klarna "pay in 4" instalment callout below the product price
  // (variant vs. control).
  //
  // Runs independently alongside pdp_v1 (headline/CTA test). Shares the
  // visitor ID (__x4_vid) so both experiments produce consistent assignments
  // for the same visitor, but the bucket split is calculated separately so
  // a visitor in "variant" for pdp_v1 is not guaranteed to be "variant" here.
  //
  // QA overrides:
  //   ?exp_pdp_m1=variant   force variant experience
  //   ?exp_pdp_m1=control   force control experience
  //
  // ⚠️  KLARNA_TEXT — update if Klarna pricing/messaging changes
  // ---------------------------------------------------------------------------

  const EXPERIMENT_ID        = 'pdp_m1';
  const EXPERIMENT_PARAM     = 'exp_pdp_m1';
  const EXPERIMENT_STORAGE_KEY = '__x4_exp_pdp_m1';
  const VISITOR_STORAGE_KEY  = '__x4_vid';           // shared with pdp_v1
  const TTL_DAYS             = 30;
  const ALLOWED_HANDLES      = new Set(['3x4-health', '3x4-blueprint']);
  const VARIANTS             = ['control', 'variant'];

  // Must match official Klarna on-site messaging plugin text.
  const KLARNA_TEXT = 'From $27/mo or 4 payments at 0% interest with Klarna';

  // ---------------------------------------------------------------------------
  // Guard — only run on the two allowed product pages
  // ---------------------------------------------------------------------------

  function getProductHandleFromPath() {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts.length < 2 || parts[0] !== 'products') return null;
    return decodeURIComponent(parts[1]).toLowerCase();
  }

  const productHandle = getProductHandleFromPath();
  if (!productHandle || !ALLOWED_HANDLES.has(productHandle)) return;

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function parseJson(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  // ---------------------------------------------------------------------------
  // Variant persistence (localStorage + cookie fallback)
  // ---------------------------------------------------------------------------

  function readStoredVariant() {
    let stored = null;
    try {
      stored = parseJson(localStorage.getItem(EXPERIMENT_STORAGE_KEY));
    } catch (e) { stored = null; }

    if (!stored || !stored.variant) {
      try {
        stored = parseJson(getCookie(EXPERIMENT_STORAGE_KEY));
      } catch (e) { stored = null; }
    }

    if (!stored || !stored.variant || !stored.expires_at) return null;
    if (Date.now() > stored.expires_at) return null;
    return stored.variant;
  }

  function writeStoredVariant(variant) {
    const payload = {
      variant:     variant,
      assigned_at: Date.now(),
      expires_at:  Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000
    };
    try {
      localStorage.setItem(EXPERIMENT_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) { /* no-op */ }

    // Mirror to cookie so the Shopify Web Pixel can read it at checkout.
    setCookie(EXPERIMENT_STORAGE_KEY, JSON.stringify(payload), TTL_DAYS);
  }

  // ---------------------------------------------------------------------------
  // QA override via URL param
  // ---------------------------------------------------------------------------

  function getOverrideVariant() {
    const params = new URLSearchParams(window.location.search);
    const forced = (params.get(EXPERIMENT_PARAM) || '').toLowerCase();
    return VARIANTS.includes(forced) ? forced : null;
  }

  // ---------------------------------------------------------------------------
  // Visitor ID — shared with pdp_v1 for cross-experiment tracking
  // ---------------------------------------------------------------------------

  function generateVisitorId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `x4-${Date.now()}-${Math.floor(Math.random() * 1e8)}`;
  }

  function getVisitorId() {
    try {
      const localId = localStorage.getItem(VISITOR_STORAGE_KEY);
      if (localId) return localId;
    } catch (e) { /* no-op */ }

    const cookieId = getCookie(VISITOR_STORAGE_KEY);
    if (cookieId) return cookieId;

    const newId = generateVisitorId();
    try { localStorage.setItem(VISITOR_STORAGE_KEY, newId); } catch (e) { /* no-op */ }
    setCookie(VISITOR_STORAGE_KEY, newId, 365);
    return newId;
  }

  // ---------------------------------------------------------------------------
  // Bucketing — independent hash from pdp_v1
  // ---------------------------------------------------------------------------

  function hashString(input) {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = (hash << 5) - hash + input.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  function chooseVariant() {
    const bucket = hashString(`${EXPERIMENT_ID}:${getVisitorId()}`) % 2;
    return bucket === 0 ? 'control' : 'variant';
  }

  // ---------------------------------------------------------------------------
  // DOM class/attribute markers (applied immediately, before DOMContentLoaded)
  // ---------------------------------------------------------------------------

  function applyVariantToDom(variant) {
    document.documentElement.dataset.expPdpM1 = variant;
    if (document.body) {
      document.body.classList.add(`exp-pdp-m1-${variant}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Analytics
  // ---------------------------------------------------------------------------

  function publishEvent(eventName, payload) {
    const base = {
      experiment_id: EXPERIMENT_ID,
      variant_id:    resolvedVariant,
      product_handle: productHandle,
      page_type:     'product',
      timestamp:     new Date().toISOString()
    };
    const merged = Object.assign(base, payload || {});

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try { window.Shopify.analytics.publish(eventName, merged); } catch (e) { /* no-op */ }
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, merged));
  }

  function publishAssignmentOnce(source) {
    const sessionKey = `__x4_exp_pdp_m1_assigned_${productHandle}`;
    let already = null;
    try { already = sessionStorage.getItem(sessionKey); } catch (e) { already = null; }
    if (already === resolvedVariant) return;
    try { sessionStorage.setItem(sessionKey, resolvedVariant); } catch (e) { /* no-op */ }
    publishEvent('exp_variant_assigned', { assignment_source: source });
  }

  // ---------------------------------------------------------------------------
  // Styles — injected once when variant is applied
  // ---------------------------------------------------------------------------

  function injectStyles() {
    if (document.getElementById('x4-exp-m1-styles')) return;
    const style = document.createElement('style');
    style.id = 'x4-exp-m1-styles';
    style.textContent = `

      /* ---- Klarna callout ---- */
      .pdp-klarna-callout {
        margin: 6px 0 0;
        font-size: 12.5px;
        color: #5f5a74;
        line-height: 1.4;
      }

      .pdp-klarna-callout__amount {
        font-weight: 700;
        color: #1f2130;
      }

      .pdp-klarna-callout__brand {
        font-weight: 700;
        color: #17120f;
        letter-spacing: -0.01em;
      }

    `;
    document.head.appendChild(style);
  }

  // ---------------------------------------------------------------------------
  // Klarna "pay in 4" callout — injected after the .price div
  // inside the price-reviews block.
  // ---------------------------------------------------------------------------

  function applyKlarnaCallout() {
    const priceBlock = document.querySelector('[id^="product-price-"] .price');
    if (!priceBlock || priceBlock.querySelector('.pdp-klarna-callout')) return;

    const callout = document.createElement('p');
    callout.className = 'pdp-klarna-callout';
    callout.textContent = KLARNA_TEXT;

    priceBlock.insertAdjacentElement('afterend', callout);
  }

  // ---------------------------------------------------------------------------
  // Remove variant injections (restore control state)
  // ---------------------------------------------------------------------------

  function removeVariantContent() {
    document.querySelectorAll('.pdp-klarna-callout').forEach((el) => el.remove());
  }

  // ---------------------------------------------------------------------------
  // Master apply function
  // ---------------------------------------------------------------------------

  function applyVariantContent() {
    if (resolvedVariant === 'variant') {
      injectStyles();
      applyKlarnaCallout();
    } else {
      removeVariantContent();
    }
  }

  // ---------------------------------------------------------------------------
  // Resolve variant and run
  // ---------------------------------------------------------------------------

  const forcedVariant = getOverrideVariant();
  let resolvedVariant = forcedVariant || readStoredVariant();
  let assignmentSource = 'persisted';

  if (!resolvedVariant) {
    resolvedVariant = chooseVariant();
    writeStoredVariant(resolvedVariant);
    assignmentSource = 'generated';
  } else if (forcedVariant) {
    assignmentSource = 'query_override';
  }

  if (!VARIANTS.includes(resolvedVariant)) resolvedVariant = 'control';

  // Apply class/data markers immediately (before DOMContentLoaded) to
  // minimise any layout shift on variant pages.
  applyVariantToDom(resolvedVariant);

  window.__x4ExperimentM1 = {
    experiment_id:  EXPERIMENT_ID,
    variant_id:     resolvedVariant,
    product_handle: productHandle
  };

  document.addEventListener('DOMContentLoaded', () => {
    applyVariantToDom(resolvedVariant);
    applyVariantContent();
    publishAssignmentOnce(assignmentSource);

    // Track ATC clicks — same data-exp-primary-cta hook used by pdp_v1.
    // Both experiments share the attribute; each publishes its own event.
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const cta = event.target.closest('[data-exp-primary-cta]');
      if (!cta) return;
      publishEvent('exp_cta_clicked', {
        cta_name: cta.getAttribute('name') || 'add_to_cart'
      });
    });

    // Re-apply if Shopify re-renders the product form after a variant switch.
    document.addEventListener('variant:changed', () => {
      applyVariantContent();
    });
  });
})();
